import {connect} from 'react-redux';

import {AsyncHandler, Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {randomString} from '../../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {ResolveDispatchProps} from '../../../../helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../helpers/reselectHelpers';
import {
  SchedulingLinkAvailabilityTypesEnum,
  SchedulingLinkModel,
} from '../../../../models/api/schedulingLinkModel';
import {
  createSchedulingCalendarsPanelTransitionAction,
  saveSchedulingLinkAsyncAction,
  updateDraftSchedulingLinkAction,
} from '../../../../store/panels/calendars/scheduling/calendarsPanelSchedulingActions';
import {
  getDidEditDraftSchedulingLink,
  getDraftSchedulingLink,
  getOtherSlugs,
  getSchedulingPanel,
  getSchedulingPanelIsPopout,
  getSchedulingPanelShouldForcePopout,
  getSchedulingStatus,
} from '../../../../store/panels/calendars/scheduling/calendarsPanelSchedulingSelectors';
import {setPanelAction} from '../../../../store/panels/panelsActions';
import {CalendarsPanelId, ComposerPanelId, ViewerPanelId} from '../../../../store/panels/panelStateBase';
import {inLegacyTransientPanel} from '../../../legacyTransientPanel';
import {
  SchedulingLinkSettingsDialog,
  SchedulingLinkSettingsDialogPublicProps,
} from './schedulingLinkSettingsDialog';

/*
 * Props.
 */

type SchedulingLinkId = number;
interface SchedulingLinkSettingsDialogContainerProps extends SchedulingLinkSettingsDialogPublicProps {
  panelId: CalendarsPanelId;
  parentPanelId: CalendarsPanelId | ComposerPanelId | ViewerPanelId;
  onSchedulingLinkCreated?: HandlerOf<SchedulingLinkId>;
  onSchedulingLinkSaved?: Handler;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SchedulingLinkSettingsDialogContainerProps>()(() => ({
  didEditDraft: getDidEditDraftSchedulingLink,
  draftLink: getDraftSchedulingLink,
  status: getSchedulingStatus,
  otherSlugs: getOtherSlugs,
  schedulingPanel: getSchedulingPanel,
  shouldForcePopout: getSchedulingPanelShouldForcePopout,
  isPopout: getSchedulingPanelIsPopout,
}));

const mapDispatchToProps = makeMapDispatchToProps<SchedulingLinkSettingsDialogContainerProps>()(
  ({panelId}) => ({
    onUpdateSchedulingLink: (changes) => updateDraftSchedulingLinkAction(panelId, changes),
    saveSchedulingLinkAsync: (draftLink) => saveSchedulingLinkAsyncAction(panelId, draftLink),
    updatePanel: setPanelAction,
  }),
);

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: SchedulingLinkSettingsDialogContainerProps,
) {
  const {draftLink, schedulingPanel} = stateProps;
  const {saveSchedulingLinkAsync, updatePanel} = dispatchProps;

  const onSaveClick: AsyncHandler<SchedulingLinkModel> = async () => {
    const isCreateRequest = draftLink.id === undefined;
    const schedulingLink = await saveSchedulingLinkAsync({
      ...draftLink,
      slug: draftLink.slug || randomString(8),
    });

    if (ownProps.onSchedulingLinkCreated && isCreateRequest) {
      ownProps.onSchedulingLinkCreated(schedulingLink.id);
    }

    if (ownProps.onSchedulingLinkSaved) {
      ownProps.onSchedulingLinkSaved();
    }

    return schedulingLink;
  };

  const onOpenPopout: Handler = () => {
    updatePanel({
      ...schedulingPanel,
      shouldForcePopout: true,
    });
  };

  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    onSaveClick,
    onOpenPopout,
  };
}

/*
 * Panel.
 */

export const SchedulingLinkSettingsDialogContainer = inLegacyTransientPanel(
  connect(mapStateToProps, mapDispatchToProps, mergeProps)(SchedulingLinkSettingsDialog),
  ({panelId, parentPanelId, schedulingLinkId}) =>
    createSchedulingCalendarsPanelTransitionAction(panelId, parentPanelId, schedulingLinkId, {
      availability: {
        type: SchedulingLinkAvailabilityTypesEnum.REPEATING,
        blocks: [],
      },
    }),
);
